import React, { Fragment } from 'react';
import {
  Outlet,
  createRootRoute,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { init as initialiseSentry } from '@sentry/react';

import { LoadGoogleTagManagerService } from '@reckon-web/google-tag-manager';
import { LoadBranchIoService } from '@reckon-web/branchio';
import { useEnsureSupportedBrowser } from '@reckon-web/next-unsupported-browsers';

import { ServicesProvider } from '../services';
import { Config } from '../services/Config';
import { LoadPendoService } from '../services/Pendo/loadPendoService';
import { Routes } from '../services/Navigation/Routes';
import { BalanceProvider } from '../components/app/Balance';
import { DevLauncherProvider } from '../components/app/DevLauncher';
import { MetaHead } from '../components/app/MetaHead';
import { AppWindow } from '../components/app/AppLayout';

initialiseSentry({
  dsn: Config.APPLICATION_SENTRY_DSN,
  environment: Config.STAGE,
  release: Config.APPLICATION_VERSION,
});

(async () => {
  if ('undefined' === typeof window) {
    return;
  }

  LoadGoogleTagManagerService({
    apiKey: Config.APPLICATION_GOOGLE_GTM_APIKEY,
  });
  LoadBranchIoService({
    apiKey: Config.APPLICATION_BRANCHIO_APIKEY,
  });
  // Start the agent downloading now, but don't boot it here: initialize()
  // needs a visitor id and auth hasn't resolved at module scope. Booting it
  // without one mints an anonymous visitor in the Pendo subscription we share
  // with portal. <PendoIdentity /> initializes once the user is known.
  LoadPendoService();
})();

export const Route = createRootRoute({
  component: RootComponent,
  staticData: {
    secure: false,
  },
});

function RootComponent() {
  const navigate = useNavigate();
  const pathname = useRouterState({
    select: (state) => {
      return state.location.pathname;
    },
  });
  const currentPath = useRouterState({
    select: (state) => {
      return state.location.href;
    },
  });

  useEnsureSupportedBrowser({
    errorPagePath: Routes.unsupportedBrowser.path,
    currentPath: pathname,
    gotoErrorPage: () => {
      navigate({ to: Routes.unsupportedBrowser.path });
    },
  });

  return (
    <Fragment>
      <BalanceProvider>
        <AppWindow>
          <ServicesProvider>
            <MetaHead
              appName={Config.APPLICATION_DISPLAY_NAME}
              version={Config.APPLICATION_VERSION?.toString() ?? ''}
            >
              <DevLauncherProvider
                currentPath={currentPath}
                visible={Config.APPLICATION_FEATURE_USE_DEVLAUNCHER}
              >
                <Outlet />
              </DevLauncherProvider>
            </MetaHead>
          </ServicesProvider>
        </AppWindow>
      </BalanceProvider>
    </Fragment>
  );
}
